import React, { ChangeEvent, FC } from 'react';
import FormInputField from 'components/FormInputField';
import { colors } from 'constants/colors';
import { FieldsRow } from './style';

import {
  COUNTRIES,
  filterOptions,
  getProvinceOptions,
  hasExactOption
} from './options';

type CountryProvinceFieldsProps = {
  idPrefix: string;

  countryValue: string;
  provinceValue: string;

  onCountryChange: (value: string) => void;
  onProvinceChange: (value: string) => void;

  countryError?: boolean;
  countryErrorMessage?: string;

  provinceError?: boolean;
  provinceErrorMessage?: string;

  countryRequired?: boolean;
  provinceRequired?: boolean;

  countryLabel?: string;
  provinceLabel?: string;

  showCountryField?: boolean;
  showProvinceField?: boolean;

  borderColor?: string;
  inputHeight?: string;
  marginBottom?: string;

  className?: string;
  inputClassName?: string;
  labelWeight?: string;

  countryPlaceholder?: string;
  provincePlaceholder?: string;

  placeholderFontSize?: string;
  placeholderColor?: string;
  
  countryInnerLabel?: string;
  provinceInnerLabel?: string;

  inputFontSize?: string;

  onCountryBlur?: React.FocusEventHandler<HTMLInputElement>;
  onProvinceBlur?: React.FocusEventHandler<HTMLInputElement>;
};

const normalizeValue = (value: string): string =>
  value.trim().toLowerCase();

const CountryProvinceFields: FC<CountryProvinceFieldsProps> = ({
  idPrefix,

  countryValue,
  provinceValue,

  onCountryChange,
  onProvinceChange,

  countryError = false,
  countryErrorMessage = '',

  provinceError = false,
  provinceErrorMessage = '',

  countryRequired = true,
  provinceRequired = true,

  countryLabel = 'COUNTRY',
  provinceLabel = 'PROVINCE / STATE',

  showCountryField = true,
  showProvinceField = true,

  borderColor = colors.signInBorderColor,
  inputHeight = '48px',
  marginBottom = '20px',
  className,
  inputClassName = 'secInput',
  labelWeight,

  countryPlaceholder = 'Search...',
  provincePlaceholder = 'Search...',

  placeholderFontSize,
  placeholderColor,

  onCountryBlur,
  onProvinceBlur,
  countryInnerLabel,
  provinceInnerLabel,
  inputFontSize


}) => {
  const countryListId = `${idPrefix}-country-options`;
  const provinceListId = `${idPrefix}-province-options`;

  const provinceOptions = getProvinceOptions(countryValue);

  const filteredCountryOptions = filterOptions(
    COUNTRIES,
    countryValue
  );

  const filteredProvinceOptions = filterOptions(
    provinceOptions,
    provinceValue
  );

  const countryIsSelected = hasExactOption(
    COUNTRIES,
    countryValue
  );

  const provinceIsSelected = hasExactOption(
    provinceOptions,
    provinceValue
  );

  const showCountryOptions =
    showCountryField &&
    countryValue.trim().length > 0 &&
    !countryIsSelected &&
    filteredCountryOptions.length > 0;

  const showProvinceOptions =
    showProvinceField &&
    provinceValue.trim().length > 0 &&
    !provinceIsSelected &&
    filteredProvinceOptions.length > 0;

  const handleCountryChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const nextCountry = e.target.value;

    const countryWasChanged =
      normalizeValue(nextCountry) !==
      normalizeValue(countryValue);

    const exactCountryWasEntered = hasExactOption(
      COUNTRIES,
      nextCountry
    );

    onCountryChange(nextCountry);

    // Не очищаем Province / State при вводе каждой буквы.
    // Очищаем только после выбора или полного ввода страны.
    if (countryWasChanged && exactCountryWasEntered) {
      onProvinceChange('');
    }
  };

  const handleProvinceChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    onProvinceChange(e.target.value);
  };

  return (
    <FieldsRow className={className}>
      {showProvinceField && (
        <div className="countryProvinceField">
          <FormInputField
            inputFontSize = {inputFontSize}
            className={inputClassName}
            label={provinceLabel}
            labelWeight={labelWeight}
            isRequired={provinceRequired}
            type="text"
            list={
              showProvinceOptions
                ? provinceListId
                : undefined
            }
            autoComplete="off"
            placeholder={provincePlaceholder}
            placeholderColor={placeholderColor}
            placeholderFontSize={placeholderFontSize}
            onBlur={onProvinceBlur}
            borderColor={borderColor}
            value={provinceValue}
            error={provinceError}
            errorMessage={provinceErrorMessage}
            inputHeight={inputHeight}
            marginBottom={marginBottom}
            onChange={handleProvinceChange}
            innerLabel={provinceInnerLabel}
          />

          {showProvinceOptions && (
            <datalist id={provinceListId}>
              {filteredProvinceOptions.map((province) => (
                <option
                  key={province}
                  value={province}
                />
              ))}
            </datalist>
          )}
        </div>
      )}

      {showCountryField && (
        <div className="countryProvinceField">
          <FormInputField
            inputFontSize={inputFontSize}
            className={inputClassName}
            labelWeight={labelWeight}
            label={countryLabel}
            isRequired={countryRequired}
            type="text"
            list={
              showCountryOptions
                ? countryListId
                : undefined
            }
            autoComplete="off"
            placeholder={countryPlaceholder}
            placeholderColor={placeholderColor}
            placeholderFontSize={placeholderFontSize}
            borderColor={borderColor}
            value={countryValue}
            error={countryError}
            errorMessage={countryErrorMessage}
            inputHeight={inputHeight}
            marginBottom={marginBottom}
            onChange={handleCountryChange}
            onBlur={onCountryBlur}
            innerLabel={countryInnerLabel}
          />

          {showCountryOptions && (
            <datalist id={countryListId}>
              {filteredCountryOptions.map((country) => (
                <option
                  key={country}
                  value={country}
                />
              ))}
            </datalist>
          )}
        </div>
      )}
    </FieldsRow>
  );
};

export default CountryProvinceFields;